import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  getRouteApi,
  useRouter,
} from "@tanstack/react-router";
import { ChevronRight, Plus } from "lucide-react";
import { useState } from "react";

import { GroupEditor } from "@/components/settings/group-editor";
import { SettingsSection } from "@/components/settings/settings-section";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { queryClient } from "@/lib/query-client";
import { can, forgetSession } from "@/lib/session";
import {
  fetchGroups,
  fetchStaff,
  groupsQueryKey,
  staffQueryKey,
} from "@/lib/settings-api";
import type { StaffGroup, StaffMember } from "@/lib/settings-api";

type Editing = { mode: "create" } | { groupId: string; mode: "edit" };

const countLabel = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

const memberCounts = (members: StaffMember[]): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const member of members) {
    counts.set(member.groupId, (counts.get(member.groupId) ?? 0) + 1);
  }
  return counts;
};

interface GroupRowProps {
  group: StaffGroup;
  isOwnGroup: boolean;
  memberCount: number | null;
  onOpen: (groupId: string) => void;
}

const GroupRow = ({
  group,
  isOwnGroup,
  memberCount,
  onOpen,
}: GroupRowProps) => {
  const details = [
    memberCount === null ? null : countLabel(memberCount, "member", "members"),
    countLabel(group.permissions.length, "permission", "permissions"),
  ].filter(Boolean);

  return (
    <li className="border-b last:border-b-0 first:*:rounded-t-2xl last:*:rounded-b-2xl">
      <button
        className="flex min-h-16 w-full items-center gap-4 px-4 py-3 text-left transition-colors outline-none hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:ring-3 focus-visible:ring-ring/30 sm:px-5"
        onClick={() => onOpen(group.id)}
        type="button"
      >
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="flex items-center gap-2 truncate text-sm font-medium">
            {group.name}
            {isOwnGroup ? <Badge variant="outline">You</Badge> : null}
          </span>
          <span className="truncate text-sm text-muted-foreground tabular-nums">
            {details.join(" · ")}
          </span>
        </span>
        <ChevronRight aria-hidden className="size-4 text-muted-foreground" />
      </button>
    </li>
  );
};

const settingsRoute = getRouteApi("/admin/settings");

const GroupsPage = () => {
  const { session } = settingsRoute.useRouteContext();
  const router = useRouter();
  const client = useQueryClient();
  const [editing, setEditing] = useState<Editing>({ mode: "create" });
  const [dialogOpen, setDialogOpen] = useState(false);

  const canReadStaff = can(session, "staff:read");
  const canWrite = can(session, "groups:write");
  const canDelete = can(session, "groups:delete");

  const groupsQuery = useQuery({
    enabled: can(session, "groups:read"),
    queryFn: fetchGroups,
    queryKey: groupsQueryKey,
  });
  const staffQuery = useQuery({
    enabled: canReadStaff,
    queryFn: fetchStaff,
    queryKey: staffQueryKey,
  });

  const groups = groupsQuery.data ?? [];
  const counts = staffQuery.data ? memberCounts(staffQuery.data) : null;
  const ownGroupId = session.staff?.groupId;

  const editingGroup =
    editing.mode === "edit"
      ? (groups.find((group) => group.id === editing.groupId) ?? null)
      : null;

  const openEditor = (next: Editing) => {
    setEditing(next);
    setDialogOpen(true);
  };

  const refresh = () => client.invalidateQueries({ queryKey: groupsQueryKey });

  const saved = async (ownGroup: boolean) => {
    await refresh();
    if (ownGroup) {
      forgetSession();
      await router.invalidate();
    }
  };

  return (
    <SettingsSection
      action={
        canWrite ? (
          <Button
            className="min-h-11"
            onClick={() => openEditor({ mode: "create" })}
          >
            <Plus aria-hidden data-icon="inline-start" />
            New group
          </Button>
        ) : null
      }
      title="Groups"
    >
      {groupsQuery.isPending ? (
        <p className="p-5 text-sm text-muted-foreground">Loading groups...</p>
      ) : (
        <ul>
          {groups.map((group) => (
            <GroupRow
              group={group}
              isOwnGroup={group.id === ownGroupId}
              key={group.id}
              memberCount={counts ? (counts.get(group.id) ?? 0) : null}
              onOpen={(groupId) => openEditor({ groupId, mode: "edit" })}
            />
          ))}
        </ul>
      )}

      <Dialog onOpenChange={setDialogOpen} open={dialogOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingGroup?.name ?? "New group"}</DialogTitle>
          </DialogHeader>
          <GroupEditor
            canDelete={canDelete}
            canWrite={canWrite}
            group={editingGroup}
            isOwnGroup={editingGroup !== null && editingGroup.id === ownGroupId}
            key={
              editingGroup
                ? `${editingGroup.id}:${editingGroup.version}`
                : "new"
            }
            onChanged={refresh}
            onClose={() => setDialogOpen(false)}
            onSaved={saved}
          />
        </DialogContent>
      </Dialog>
    </SettingsSection>
  );
};

export const Route = createFileRoute("/admin/settings/groups")({
  component: GroupsPage,
  loader: async ({ context }) => {
    if (!can(context.session, "groups:read")) {
      return;
    }
    await Promise.all([
      queryClient.ensureQueryData({
        queryFn: fetchGroups,
        queryKey: groupsQueryKey,
      }),
      can(context.session, "staff:read")
        ? queryClient.ensureQueryData({
            queryFn: fetchStaff,
            queryKey: staffQueryKey,
          })
        : null,
    ]);
  },
});
